import { useEffect } from 'react';

function ensureMeta(selector, attributes) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement('meta');
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));
  return element;
}

function absoluteUrl(value, fallback) {
  const path = value || fallback;
  if (/^https?:\/\//i.test(path)) return path;
  return `https://joinjkl.com${path.startsWith('/') ? path : `/${path}`}`;
}

export default function useInsideSeo({
  title,
  description,
  path = '/inside',
  type = 'website',
  article = null,
  image = null,
  noindex = false,
}) {
  useEffect(() => {
    const fullTitle = title ? `${title} | JKL / INSIDE` : 'JKL / INSIDE — Storie e mestieri della post-produzione televisiva';
    const canonicalUrl = `https://joinjkl.com${path}`;
    const imageUrl = absoluteUrl(image || article?.image, '/inside-og/inside-home.png');
    const isStaging = window.location.hostname.startsWith('staging.');
    const shouldNoindex = isStaging || noindex;

    document.documentElement.lang = 'it';
    document.title = fullTitle;
    ensureMeta('meta[name="description"]', { name: 'description', content: description });
    ensureMeta('meta[name="robots"]', {
      name: 'robots',
      content: shouldNoindex ? 'noindex,nofollow' : 'index,follow,max-image-preview:large',
    });
    ensureMeta('meta[property="og:title"]', { property: 'og:title', content: fullTitle });
    ensureMeta('meta[property="og:description"]', { property: 'og:description', content: description });
    ensureMeta('meta[property="og:type"]', { property: 'og:type', content: type });
    ensureMeta('meta[property="og:url"]', { property: 'og:url', content: canonicalUrl });
    ensureMeta('meta[property="og:site_name"]', { property: 'og:site_name', content: 'JKL / INSIDE' });
    ensureMeta('meta[property="og:locale"]', { property: 'og:locale', content: 'it_IT' });
    ensureMeta('meta[property="og:image"]', { property: 'og:image', content: imageUrl });
    ensureMeta('meta[property="og:image:width"]', { property: 'og:image:width', content: '1200' });
    ensureMeta('meta[property="og:image:height"]', { property: 'og:image:height', content: '630' });
    ensureMeta('meta[property="og:image:alt"]', { property: 'og:image:alt', content: article?.title || 'JKL / INSIDE' });
    ensureMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
    ensureMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: fullTitle });
    ensureMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: description });
    ensureMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: imageUrl });

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = canonicalUrl;

    const oldSchema = document.getElementById('inside-jsonld');
    oldSchema?.remove();
    const schema = document.createElement('script');
    schema.id = 'inside-jsonld';
    schema.type = 'application/ld+json';
    schema.text = JSON.stringify(article ? {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.subtitle,
      image: [imageUrl],
      datePublished: article.datePublished,
      dateModified: article.dateModified || article.datePublished,
      mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl },
      author: { '@type': 'Organization', name: 'JKL / INSIDE', url: 'https://joinjkl.com/inside' },
      publisher: {
        '@type': 'Organization',
        name: 'JKL — The Private Network',
        url: 'https://joinjkl.com',
        logo: { '@type': 'ImageObject', url: 'https://joinjkl.com/Logo_JKL.png' },
      },
      articleSection: article.category,
      inLanguage: 'it-IT',
    } : {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'JKL / INSIDE',
      description,
      url: canonicalUrl,
      image: imageUrl,
      isPartOf: { '@type': 'WebSite', name: 'JKL — The Private Network', url: 'https://joinjkl.com' },
      inLanguage: 'it-IT',
    });
    document.head.appendChild(schema);

    return () => schema.remove();
  }, [title, description, path, type, article, image, noindex]);
}
